import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEmailMarketingV1SenderDetails from './email-marketing-v1-sender-details-sender-details.http.js';

/**
 * A sender details object contains the information that is displayed as the email sender's name and email address.
 * If sending email campaigns with a public email domain (e.g. @gmail.com or @yahoo.com), the email address will not
 * be displayed in the 'from' header. Instead, the email address is replaced with @wixemails.com (or @wixsitemail.com for free users),
 * and the sender's email is placed in the 'reply-to' header.
 *
 * If sending email campaigns with an email from a Wix managed domain, that email will be displayed in the 'from' header.
 * If sending with an email from a custom domain that is not managed by Wix, a line will be inserted that states that the
 * email was sent via wixemails.com.
 */
export interface SenderDetails {
  /** Display name of sender. */
  fromName?: string;
  /**
   * Email address of sender.
   * @format EMAIL
   */
  fromEmail?: string;
  /**
   * Date and time the sender's email address was verified.
   * @readonly
   */
  dateVerified?: Date | null;
}

export interface GetSenderDetailsRequest {}

export interface GetSenderDetailsResponse {
  /** Current sender details. */
  senderDetails?: SenderDetails;
}

export interface UpdateSenderDetailsRequest {
  /** New sender details. */
  senderDetails: SenderDetails;
}

export interface UpdateSenderDetailsResponse {
  /** Whether email address needs verification. */
  verificationNeeded?: boolean;
}

export interface VerifySenderEmailRequest {
  /** Verification code. */
  verificationCode: string;
}

export interface VerifySenderEmailResponse {}

export interface ResolveActualFromAddressRequest {
  /**
   * User's provided address from which to send email marketing campaign.
   * @format EMAIL
   */
  fromAddress: string;
}

export interface ResolveActualFromAddressResponse {
  /**
   * Actual from-address that will be used for email distribution.
   * @format EMAIL
   */
  actualFromAddress?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type VerifyEmailApplicationErrors = {
  code?: 'INVALID_VERIFICATION_CODE';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface SenderDetailsUpdatedEnvelope {
  entity: SenderDetails;
  metadata: EventMetadata;
}

/**
 * An event that triggers when sender details are updated without the need for explicit verification.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Read Email Marketing
 * @permissionScopeId SCOPE.DC-PROMOTE.READ-EMAIL-MARKETING
 * @permissionScope Manage Email Marketing
 * @permissionScopeId SCOPE.DC-PROMOTE.EMAIL-MARKETING
 * @permissionId Shoutout.Read
 * @webhook
 * @eventType wix.email_marketing.v1.sender_details_updated
 * @serviceIdentifier wix.emailmarketing.api.v1.SenderDetailsService
 * @slug updated
 */
export declare function onSenderDetailsUpdated(
  handler: (event: SenderDetailsUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves sender details.
 *
 * The sender details include the information displayed as the email sender's name and email address.
 *
 * + If you send an email campaign with a public email domain (e.g. @gmail.com or @yahoo.com), the email address will not be displayed in the 'from' header. Instead, the email address is replaced with @wixemails.com (or @wixsitemail.com for free users), and the sender's email is placed in the 'reply-to' header.
 * + If you send an email campaign with an email from a Wix managed domain, that email will be displayed in the 'from' header.
 * + If you send an email from a custom domain that is not managed by Wix, a line will be inserted that states that the email was sent via wixemails.com.
 * @public
 * @permissionId Shoutout.Manage
 * @applicableIdentity APP
 * @fqn wix.emailmarketing.api.v1.SenderDetailsService.Get
 * @deprecated
 * @replacedBy wix.promote.v1.SenderDetailsService.GetDefaultSenderDetails
 * @targetRemovalDate 2025-09-30
 */
export async function getSenderDetails(): Promise<
  NonNullablePaths<
    GetSenderDetailsResponse,
    `senderDetails.fromName` | `senderDetails.fromEmail`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixEmailMarketingV1SenderDetails.get(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates sender details (only provided fields will be updated).
 *
 * If `from_email` is changed - a verification code will be sent to new email address.
 * If verification is needed, a verification email will be sent to the user, and `verification_needed` will return true.
 * @param senderDetails - New sender details.
 * @public
 * @requiredField senderDetails
 * @permissionId Shoutout.Manage
 * @applicableIdentity APP
 * @fqn wix.emailmarketing.api.v1.SenderDetailsService.Update
 * @deprecated
 * @replacedBy wix.promote.v1.SenderEmailService.GetOrCreateSenderEmail
 * @targetRemovalDate 2025-09-30
 */
export async function updateSenderDetails(
  senderDetails: SenderDetails
): Promise<
  NonNullablePaths<UpdateSenderDetailsResponse, `verificationNeeded`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    senderDetails: senderDetails,
  });

  const reqOpts = ambassadorWixEmailMarketingV1SenderDetails.update(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { senderDetails: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['senderDetails']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Verifies the sender's email using a verification code sent to the user's email address upon update.
 * @param verificationCode - Verification code.
 * @public
 * @requiredField verificationCode
 * @permissionId Shoutout.Manage
 * @applicableIdentity APP
 * @fqn wix.emailmarketing.api.v1.SenderDetailsService.VerifyEmail
 * @deprecated
 * @replacedBy wix.promote.v1.SenderEmailService.VerifySenderEmail
 * @targetRemovalDate 2025-09-30
 */
export async function verifyEmail(verificationCode: string): Promise<
  void & {
    __applicationErrorsType?: VerifyEmailApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    verificationCode: verificationCode,
  });

  const reqOpts =
    ambassadorWixEmailMarketingV1SenderDetails.verifyEmail(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { verificationCode: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['verificationCode']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Checks if the sender's email address will be used as from-address or replaced (not related to current sender details).
 * @param fromAddress - User's provided address from which to send email marketing campaign.
 * @public
 * @requiredField fromAddress
 * @permissionId Shoutout.Manage
 * @applicableIdentity APP
 * @fqn wix.emailmarketing.api.v1.SenderDetailsService.ResolveActualFromAddress
 * @deprecated
 * @replacedBy wix.emailmarketing.api.v1.CampaignService.IdentifySenderAddress
 * @targetRemovalDate 2025-09-30
 */
export async function resolveActualFromAddress(
  fromAddress: string
): Promise<
  NonNullablePaths<ResolveActualFromAddressResponse, `actualFromAddress`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    fromAddress: fromAddress,
  });

  const reqOpts =
    ambassadorWixEmailMarketingV1SenderDetails.resolveActualFromAddress(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { fromAddress: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['fromAddress']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
